import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, QrCode, ClipboardCheck, LayoutDashboard, Download, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { iconFor } from "@/lib/icons";
import { Reveal } from "@/components/Reveal";
import { BOOKING_URL } from "@/config";
import { useSeo } from "@/lib/seo";

const HERO_IMG = "https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const STEPS = [
  { icon: ClipboardCheck, title: "Diagnostic 5 min", text: "Répondez à quelques questions tactiles sur votre situation." },
  { icon: LayoutDashboard, title: "Carte de protection", text: "Visualisez vos forces et vos points à renforcer, clairement." },
  { icon: GraduationCap, title: "Formations", text: "18 modules clairs : une idée par slide, avec calculateurs." },
  { icon: Download, title: "Emportez tout", text: "Téléchargez vos modules en PPTX ou PDF pour vos rendez-vous." },
];

export default function Landing() {
  const [topics, setTopics] = useState([]);
  const [modules, setModules] = useState([]);
  useSeo("Mon Projet Protégé — diagnostic et formations financières au Québec",
    "Diagnostic 5 minutes et 18 formations claires (REEE, CELIAPP, REER, RAMQ, assurance vie) pour prendre en main votre protection financière au Québec.");

  useEffect(() => {
    api.topics().then(setTopics).catch(() => {});
    api.modules("publie").then(setModules).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blob" style={{ background: "#012141" }} />
        <div className="absolute top-40 -right-20 w-80 h-80 rounded-full blob" style={{ background: "#C89A3E" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-12 sm:pt-20 pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <Reveal className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#012141]/10 shadow-sm text-sm font-semibold text-[#012141]">
                <Sparkles size={16} style={{ color: "#C89A3E" }} /> Votre sécurité financière, clarifiée
              </Reveal>
              <Reveal as="h1" delay={80} className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#012141] leading-[1.05] font-heading">
                Prenez en main votre <span style={{ color: "#C89A3E" }}>protection financière</span> au Québec
              </Reveal>
              <Reveal as="p" delay={160} className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">
                Un diagnostic interactif en 5 minutes et 18 formations claires : REEE, CELIAPP, REER, RAMQ, assurance vie, fiscalité et plus. Simple, tactile, rassurant.
              </Reveal>
              <Reveal delay={240} className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/diagnostic" data-testid="hero-start-diagnostic-btn" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#012141] text-white font-semibold shadow-xl hover:bg-[#00172E] hover:-translate-y-0.5 transition-all">
                  Démarrer mon diagnostic gratuit <ArrowRight size={18} />
                </Link>
                <Link to="/formations" data-testid="hero-explore-formations-btn" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-[#012141] font-semibold border border-[#012141]/15 hover:-translate-y-0.5 transition-all">
                  Explorer les formations
                </Link>
              </Reveal>
              <Reveal delay={320} className="mt-7 flex items-center gap-3 text-sm text-slate-500">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#012141]/8"><QrCode size={18} style={{ color: "#012141" }} /></span>
                Pensé mobile d'abord — scannez un QR et continuez sur votre téléphone.
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={200} className="relative">
                <div className="rounded-3xl overflow-hidden border border-[#012141]/10 shadow-[0_30px_60px_-25px_rgba(18,49,92,0.4)]">
                  <img src={HERO_IMG} alt="Conseil financier au Québec" className="w-full h-[380px] sm:h-[460px] object-cover" />
                </div>
                <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-2xl border border-[#012141]/10 shadow-xl px-5 py-4 flex items-center gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#012141] text-[#C89A3E]"><ShieldCheck size={20} /></span>
                  <div>
                    <div className="text-xl font-bold text-[#012141] font-heading">18</div>
                    <div className="text-xs text-slate-500">formations claires</div>
                  </div>
                </div>
                <div className="absolute -top-4 right-2 sm:right-6 bg-white rounded-2xl border border-[#012141]/10 shadow-xl px-4 py-3 flex items-center gap-2">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#C89A3E] text-[#012141]"><Sparkles size={16} /></span>
                  <span className="text-sm font-semibold text-[#012141]">5 min chrono</span>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <Reveal className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-wider text-[#C89A3E]">Les essentiels du Québec</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#012141] font-heading">Cinq sujets qui changent tout</h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {topics.map((t, i) => {
            const Icon = iconFor({ REEE: "graduation-cap", CELIAPP: "home", REER: "landmark", RAMQ: "heart-pulse", ASSURANCE_VIE: "heart" }[t.code]);
            return (
              <Reveal key={t.code} delay={i * 70} data-testid={`topic-card-${t.code}`}
                className="group rounded-3xl bg-white border border-[#012141]/8 p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <span className="grid place-items-center w-14 h-14 rounded-2xl text-white shadow-lg" style={{ backgroundColor: "#012141" }}><Icon size={26} style={{ color: "#C89A3E" }} /></span>
                <div className="mt-5 text-sm font-bold tracking-wide" style={{ color: "#C89A3E" }}>{t.code.replace("_", " ")}</div>
                <h3 className="mt-1 text-lg font-semibold text-[#012141] font-heading leading-snug">{t.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.gap_msg}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-white border-y border-[#012141]/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
          <Reveal className="max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#012141] font-heading">Comment ça marche</h2>
            <p className="mt-3 text-base text-slate-600">Quatre étapes simples, du diagnostic à la présentation.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 80} className="rounded-3xl bg-[#FDFDFD] border border-[#012141]/8 p-6">
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#012141] text-[#C89A3E]"><s.icon size={22} /></span>
                <div className="mt-4 text-xs font-bold text-slate-400">ÉTAPE {i + 1}</div>
                <h3 className="mt-1 text-lg font-semibold text-[#012141] font-heading">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-wider text-[#C89A3E]">Formations</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#012141] font-heading">Des présentations, pas des pavés</h2>
          </Reveal>
          <Link to="/formations" data-testid="see-all-formations-btn" className="inline-flex items-center gap-2 text-[#012141] font-semibold hover:gap-3 transition-all">Voir les 18 <ArrowRight size={18} /></Link>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {modules.slice(0, 3).map((m, i) => {
            const Icon = iconFor(m.icon);
            return (
              <Reveal key={m.id} delay={i * 80} className="rounded-3xl bg-white border border-[#012141]/8 p-7 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all">
                <span className="grid place-items-center w-12 h-12 rounded-xl text-[#C89A3E]" style={{ backgroundColor: m.color }}><Icon size={22} /></span>
                <h3 className="mt-4 text-xl font-semibold text-[#012141] font-heading">{m.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{m.description}</p>
                <Link to={`/formations/${m.slug}`} data-testid={`preview-module-${m.slug}`} className="mt-5 inline-flex items-center gap-2 font-semibold text-sm text-[#012141]">Ouvrir le module <ArrowRight size={16} /></Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-16">
        <Reveal className="relative overflow-hidden rounded-3xl px-6 sm:px-12 py-12 sm:py-16 text-center" style={{ background: "#012141" }}>
          <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full blob" style={{ background: "#C89A3E" }} />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-heading">Prêt à voir votre carte de protection ?</h2>
            <p className="mt-3 text-white/70 max-w-xl mx-auto">Cinq minutes suffisent pour clarifier où vous en êtes et quoi améliorer.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/diagnostic" data-testid="cta-band-diagnostic-btn" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#C89A3E] text-[#012141] font-bold hover:brightness-95 transition-all">Faire le diagnostic <ArrowRight size={18} /></Link>
              <a href={BOOKING_URL} target="_blank" rel="noreferrer" data-testid="cta-band-booking-btn" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold text-white border border-white/30 hover:bg-white/10 transition-all">Parler à un conseiller</a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
